import { useEffect, useState, type ReactNode } from 'react'
import { STATUS_LABELS } from '../api'

export function Card({
  title,
  actions,
  children,
  tight,
}: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  tight?: boolean
}) {
  return (
    <div className="card">
      {(title || actions) && (
        <div className="hd">
          <h2>{title}</h2>
          {actions && <div className="btn-row">{actions}</div>}
        </div>
      )}
      <div className={tight ? 'bd tight' : 'bd'}>{children}</div>
    </div>
  )
}

export function Stat({
  label,
  value,
  note,
  tone,
}: {
  label: string
  value: ReactNode
  note?: ReactNode
  tone?: 'ok' | 'danger'
}) {
  return (
    <div className={`stat ${tone ?? ''}`}>
      <div className="k">{label}</div>
      <div className="v">{value}</div>
      {note && <div className="n">{note}</div>}
    </div>
  )
}

export function Badge({ status, children }: { status?: string; children?: ReactNode }) {
  const cls = status ?? 'neutral'
  return <span className={`badge ${cls}`}>{children ?? STATUS_LABELS[cls] ?? cls}</span>
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label?: string
  hint?: ReactNode
  error?: string
  children: ReactNode
}) {
  return (
    <div className="field">
      {label && <label>{label}</label>}
      {children}
      {error ? (
        <div className="hint" style={{ color: 'var(--danger)' }}>
          {error}
        </div>
      ) : (
        hint && <div className="hint">{hint}</div>
      )}
    </div>
  )
}

export function Alert({
  kind = 'info',
  children,
}: {
  kind?: 'error' | 'warn' | 'ok' | 'info'
  children: ReactNode
}) {
  if (!children) return null
  return <div className={`alert ${kind}`}>{children}</div>
}

export function Empty({ icon, title, hint }: { icon?: string; title: string; hint?: ReactNode }) {
  return (
    <div className="empty">
      {icon && <div className="big">{icon}</div>}
      <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{title}</div>
      {hint && <div style={{ marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return <div className="spinner">{label}</div>
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="overlay" onClick={onClose}>
      <div className={`modal ${wide ? 'wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="hd">
          <h2>{title}</h2>
          <button className="btn ghost sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="bd">{children}</div>
        {footer && <div className="ft">{footer}</div>}
      </div>
    </div>
  )
}

/** Copy-to-clipboard button that confirms it worked. */
export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      className="btn sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text)
        } catch {
          // Clipboard API needs a secure context; fall back to a temp textarea.
          const ta = document.createElement('textarea')
          ta.value = text
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        setDone(true)
        setTimeout(() => setDone(false), 1600)
      }}
    >
      {done ? '✓ Copied' : label}
    </button>
  )
}

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: string; label: string }[]
  active: string
  onChange: (key: string) => void
}) {
  return (
    <div className="tabs">
      {tabs.map((t) => (
        <button
          key={t.key}
          className={active === t.key ? 'active' : ''}
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

export function Confirm({
  title,
  message,
  confirmLabel = 'Confirm',
  danger,
  onConfirm,
  onCancel,
  requireReason,
}: {
  title: string
  message: ReactNode
  confirmLabel?: string
  danger?: boolean
  onConfirm: (reason: string) => void
  onCancel: () => void
  requireReason?: boolean
}) {
  const [reason, setReason] = useState('')
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className={`btn ${danger ? 'danger' : 'primary'}`}
            disabled={requireReason && reason.trim().length < 3}
            onClick={() => onConfirm(reason)}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <div>{message}</div>
      {requireReason && (
        <div style={{ marginTop: 12 }}>
          <Field label="Reason (recorded in the audit log)">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Duplicate of DigiAud2601"
              autoFocus
            />
          </Field>
        </div>
      )}
    </Modal>
  )
}
